// The SELL 1% as a recorded obligation (backend pkg/userland/sellfee).
//
// A sell is swap-only; its 1% leaves in a SEPARATE signed external after the
// sell confirms. The server holds the owed row and every signed fee attempt,
// relays the attempt itself, re-offers it until its valid_until, and accepts
// a new signature only after the chain proved the previous one cannot pay.
// The browser never relays a fee on its own — a BOC the server refused is
// never sent anywhere, which is what makes "never twice" hold.
//
// This module is light (no signing) so the app-level settler can poll it
// without pulling the TON signer into the boot graph.

import { ApiError, apiFetch } from '../api/client'

export type SellFeeRecord = {
  sell_external_hash: string
  venue: string
  proceeds_nano: string
  fee_nano: string
  status: 'owed' | 'paid'
  attempt_no: number
  attempt_outcome?: string
  /** Server-computed: a new signature would be accepted now. */
  retry_ok: boolean
  created_at: number
}

export type SellFeeAttemptResponse = {
  /** 'submitted': relayed now; 'queued': stored, the server keeps re-offering. */
  status: 'submitted' | 'queued'
  attempt_no: number
  fee_external_hash: string
  valid_until: number
}

function feesPath(walletAddress: string): string {
  return `/v1/wallets/${encodeURIComponent(walletAddress)}/sell-fees`
}

/** A hung ledger call must never hold the fee's seqno slot (and the trades
    queued behind it) forever. The attempt call waits on the server's relay. */
const LEDGER_TIMEOUT_MS = 10_000
const ATTEMPT_TIMEOUT_MS = 15_000

function timeout(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
    ? AbortSignal.timeout(ms)
    : undefined
}

export async function recordSellFee(
  walletAddress: string,
  input: { sellExternalHash: string; venue: string; proceedsNano: bigint }
): Promise<SellFeeRecord> {
  return apiFetch<SellFeeRecord>(feesPath(walletAddress), {
    method: 'POST',
    cache: 'no-store',
    signal: timeout(LEDGER_TIMEOUT_MS),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sell_external_hash: input.sellExternalHash,
      venue: input.venue,
      proceeds_nano: input.proceedsNano.toString(),
    }),
  })
}

export async function listOwedSellFees(walletAddress: string): Promise<SellFeeRecord[]> {
  const body = await apiFetch<{ items?: SellFeeRecord[] }>(feesPath(walletAddress), {
    cache: 'no-store',
    signal: timeout(LEDGER_TIMEOUT_MS),
  })
  return Array.isArray(body.items) ? body.items : []
}

/** `record` makes the server record the owed fee in the same request (the
    in-tab path); the settler's retries send the BOC alone. */
export async function submitSellFeeAttempt(
  walletAddress: string,
  sellExternalHash: string,
  signedBocBase64: string,
  record?: { venue: string; proceedsNano: bigint }
): Promise<SellFeeAttemptResponse> {
  return apiFetch<SellFeeAttemptResponse>(`${feesPath(walletAddress)}/attempts`, {
    method: 'POST',
    cache: 'no-store',
    signal: timeout(ATTEMPT_TIMEOUT_MS),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sell_external_hash: sellExternalHash,
      signed_boc_base64: signedBocBase64,
      ...(record ? { venue: record.venue, proceeds_nano: record.proceedsNano.toString() } : {}),
    }),
  })
}

/** Any 4xx from the attempts route is a refusal BEFORE storage and relay. */
export function attemptRefused(err: unknown): boolean {
  return err instanceof ApiError && err.status >= 400 && err.status < 500
}

/** The ledger route does not exist on this API (an older build). */
export function ledgerAbsent(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code !== 'sell_fee_not_found'
}

// ---- outbox: an owed fee whose record has not reached the server yet ------

const OUTBOX_KEY = 'wils-sell-fee-outbox-v1'
const OUTBOX_MAX = 200

export type SellFeeOutboxEntry = {
  wallet: string
  sellExternalHash: string
  venue: string
  proceedsNano: string
  queuedAt: number
}

function readOutbox(): SellFeeOutboxEntry[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(parsed)
      ? parsed.filter(
          (e): e is SellFeeOutboxEntry =>
            typeof e?.wallet === 'string' &&
            typeof e?.sellExternalHash === 'string' &&
            typeof e?.venue === 'string' &&
            typeof e?.proceedsNano === 'string'
        )
      : []
  } catch {
    return []
  }
}

function writeOutbox(entries: SellFeeOutboxEntry[]): void {
  if (entries.length > OUTBOX_MAX) {
    console.warn('sell fee outbox: over capacity, oldest entries lost', entries.length - OUTBOX_MAX)
  }
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries.slice(-OUTBOX_MAX)))
  } catch (err) {
    // Storage unavailable: the in-flight request still carries the record,
    // but nothing in this browser can re-record it if that request is lost.
    console.warn('sell fee outbox: storage unavailable', err)
  }
}

export function queueSellFee(entry: Omit<SellFeeOutboxEntry, 'queuedAt'>): void {
  const rest = readOutbox().filter(
    (e) => !(e.wallet === entry.wallet && e.sellExternalHash === entry.sellExternalHash)
  )
  writeOutbox([...rest, { ...entry, queuedAt: Date.now() }])
}

export function dropSellFee(wallet: string, sellExternalHash: string): void {
  const all = readOutbox()
  const rest = all.filter((e) => !(e.wallet === wallet && e.sellExternalHash === sellExternalHash))
  if (rest.length !== all.length) writeOutbox(rest)
}

export function queuedSellFees(wallet: string): SellFeeOutboxEntry[] {
  return readOutbox().filter((e) => e.wallet === wallet)
}

// ---- same-tab in-flight guard ------------------------------------------------

const inFlight = new Set<string>()

/** False when this tab is already settling that sell's fee. */
export function beginSellFeeWork(sellExternalHash: string): boolean {
  if (inFlight.has(sellExternalHash)) return false
  inFlight.add(sellExternalHash)
  return true
}

export function endSellFeeWork(sellExternalHash: string): void {
  inFlight.delete(sellExternalHash)
}
